import type { BadgeTone } from '../components/ui';
import { supabase } from './supabase';
import type { Database } from './database.types';

/**
 * Ideation — Search Console opportunities.
 *
 * Rows come from scripts/lib/seo-detect.mjs, which reads client_pages + client_page_queries and
 * names what is wrong. Nothing here detects; this is the app's view of the result.
 *
 * Deliberately no composite score anywhere in this file. A row says what kind of problem it is,
 * what it would be worth, and what the agent proposes to do — three facts a person can argue
 * with. One blended number would just get optimized until it turned green.
 */

export type SeoKind =
  | 'striking_distance'
  | 'ctr_below_curve'
  | 'wrong_page'
  | 'cannibalization'
  | 'content_gap'
  | 'decay'
  | 'zero_click_shift';

export type SeoActionType =
  | 'title_rewrite'
  | 'meta_rewrite'
  | 'internal_links'
  | 'expand'
  | 'de_optimize'
  | 'new_page'
  | 'consolidate'
  | 'no_action';

export type SeoStatus = 'open' | 'diagnosing' | 'diagnosed' | 'queued' | 'shipped' | 'measured' | 'dismissed';

export interface SeoBaseline {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  window: string | null;
}

export interface SeoOpportunity {
  id: string;
  client_id: string;
  page_id: string | null;
  kind: SeoKind;
  query: string;
  url: string | null;
  baseline: SeoBaseline;
  est_click_gain: number | null;
  action_type: SeoActionType | null;
  recommendation: string | null;
  evidence: Record<string, unknown> | null;
  model: string | null;
  diagnosed_at: string | null;
  status: SeoStatus;
  job_id: string | null;
  content_queue_id: string | null;
  shipped_at: string | null;
  result_28d: SeoBaseline | null;
  result_56d: SeoBaseline | null;
  detected_at: string;
  updated_at: string;
  /** A = commercial/transactional/local intent for a service the client sells; B = everything else relevant. */
  tier: 'A' | 'B' | null;
  tier_reason: string | null;
  /** Joined for the all-clients list. */
  client?: { id: string; name: string } | null;
}

/**
 * What each kind means in one line, for the person reading the list.
 *
 * `blurb` is written to answer "so what do I do about it" — the failure mode of every SEO tool
 * is a chip that names a condition and leaves the reader to guess the response.
 */
export const SEO_KINDS: Record<SeoKind, { label: string; tone: BadgeTone; blurb: string }> = {
  striking_distance: {
    label: 'Striking distance',
    tone: 'accent',
    blurb: 'Ranks on page two or the bottom of page one with real impressions. The biggest single jump in click share there is.',
  },
  ctr_below_curve: {
    label: 'Losing the click',
    tone: 'warn',
    blurb: 'Ranks in the top ten and still gets skipped. Usually the title or description, not the page — the cheapest fix here.',
  },
  wrong_page: {
    label: 'Wrong page',
    tone: 'info',
    blurb: 'A page on the site is titled for this query and Google chose a different URL. Internal links and anchor text, not a rewrite.',
  },
  cannibalization: {
    label: 'Cannibalized',
    tone: 'down',
    blurb: 'Two or more pages trade places on one query. Consolidate, or de-optimize the weaker one and point it at the stronger.',
  },
  content_gap: {
    label: 'Content gap',
    tone: 'neutral',
    blurb: 'Google shows the site for this and nothing on it is actually about it. This is a brief, not an edit.',
  },
  decay: {
    label: 'Decaying',
    tone: 'down',
    blurb: 'Down against both the previous window and the same period last year.',
  },
  zero_click_shift: {
    label: 'Zero-click',
    tone: 'idle',
    blurb: 'Impressions held, position held, clicks fell. The SERP took the click — often there is no on-site fix.',
  },
};

export const SEO_ACTIONS: Record<SeoActionType, { label: string; blurb: string }> = {
  title_rewrite: { label: 'Rewrite title', blurb: 'The SERP listing, not the page body.' },
  meta_rewrite: { label: 'Rewrite description', blurb: 'What the searcher reads before deciding to click.' },
  internal_links: { label: 'Add internal links', blurb: 'Point existing pages at this one with anchor text that matches the query.' },
  expand: { label: 'Expand the page', blurb: 'The page is thinner than what ranks above it.' },
  de_optimize: { label: 'De-optimize', blurb: 'The phrase is overused. Remove instances rather than adding more.' },
  new_page: { label: 'Write a new page', blurb: 'Nothing on the site serves this intent. Goes to the content queue.' },
  consolidate: { label: 'Consolidate pages', blurb: 'Merge the competing pages and redirect the loser.' },
  no_action: { label: 'No action', blurb: 'Nothing on-site would move this. Recorded so nobody re-opens it next month.' },
};

export const SEO_STATUSES: Record<SeoStatus, { label: string; tone: BadgeTone }> = {
  open: { label: 'Open', tone: 'neutral' },
  diagnosing: { label: 'In the queue', tone: 'warn' },
  diagnosed: { label: 'Diagnosed', tone: 'info' },
  queued: { label: 'Queued', tone: 'accent' },
  shipped: { label: 'Shipped', tone: 'warn' },
  measured: { label: 'Measured', tone: 'up' },
  dismissed: { label: 'Dismissed', tone: 'idle' },
};

/**
 * The four tabs on Ideation, and which statuses each one shows. One list, so the tabs and the
 * "Move to" menu can never disagree about where a status lives.
 */
export type SeoStage = 'open' | 'diagnosed' | 'queued' | 'settled';

export const SEO_STAGES: { id: SeoStage; label: string; statuses: SeoStatus[] }[] = [
  { id: 'open', label: 'Found', statuses: ['open'] },
  // Rows being read sit beside the ones already read, so a queued job is somewhere a person can
  // see it rather than nowhere at all.
  { id: 'diagnosed', label: 'Diagnosed', statuses: ['diagnosing', 'diagnosed'] },
  { id: 'queued', label: 'In flight', statuses: ['queued', 'shipped'] },
  { id: 'settled', label: 'Settled', statuses: ['measured', 'dismissed'] },
];

/**
 * Where a person can put a row by hand, and what that means. `diagnosing` is missing on purpose:
 * it says an agent job is out, and only queueing a job (Diagnose) makes that true.
 */
export const SEO_MOVE_TARGETS: Partial<Record<SeoStatus, string>> = {
  open: 'Unread. The next detection run drops it if Search Console stops showing the problem.',
  diagnosed: 'Read, with a proposal waiting to be accepted.',
  queued: 'Accepted. The fix is being built.',
  shipped: 'Live. Stamps the ship date if it has none, which starts the 28-day clock.',
  measured: 'Shipped and measured. Done.',
  dismissed: 'Not worth doing. Detection will not bring it back.',
};

export const seoStageOf = (status: SeoStatus) => SEO_STAGES.find((s) => s.statuses.includes(status)) ?? SEO_STAGES[0];

export const seoKind = (value: string) =>
  SEO_KINDS[value as SeoKind] ?? { label: value, tone: 'neutral' as BadgeTone, blurb: '' };
export const seoAction = (value: string | null) =>
  (value && SEO_ACTIONS[value as SeoActionType]) || null;
export const seoStatus = (value: string) =>
  SEO_STATUSES[value as SeoStatus] ?? { label: value, tone: 'neutral' as BadgeTone };

/**
 * Ideation's order: every A finding above every B, then untiered, then by clicks won. A B finding
 * worth more clicks still sits under an A one — the A page is the one that can turn into a customer.
 */
const TIER_RANK: Record<string, number> = { A: 0, B: 1 };
export const compareByTier = (a: Pick<SeoOpportunity, 'tier' | 'est_click_gain'>, b: Pick<SeoOpportunity, 'tier' | 'est_click_gain'>) =>
  (TIER_RANK[a.tier ?? ''] ?? 2) - (TIER_RANK[b.tier ?? ''] ?? 2) || (b.est_click_gain ?? 0) - (a.est_click_gain ?? 0);

/** Clicks per month, from a 28-day window. The number a person actually cares about. */
export const perMonth = (gain: number | null | undefined) =>
  gain == null ? null : Math.round((gain * 30.4) / 28);

export async function fetchOpportunities(filters: { clientId?: string | null; status?: SeoStatus[] } = {}) {
  let q = supabase
    .from('seo_opportunities')
    .select('*, client:clients(id, name)')
    .order('est_click_gain', { ascending: false, nullsFirst: false })
    .limit(2000);
  if (filters.clientId) q = q.eq('client_id', filters.clientId);
  if (filters.status?.length) q = q.in('status', filters.status);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as SeoOpportunity[];
}

export async function updateOpportunity(id: string, patch: Partial<SeoOpportunity>) {
  const { client: _client, ...columns } = patch;
  void _client;
  const { error } = await supabase
    .from('seo_opportunities')
    .update(columns as Database['public']['Tables']['seo_opportunities']['Update'])
    .eq('id', id);
  if (error) throw error;
}

/**
 * Move rows to a status by hand — the Move to menu on Ideation.
 *
 * `shipped_at` follows the move: going to shipped stamps it where it is empty (a row moved back and
 * forth keeps its first ship date), and going back before shipped clears it, because a row that has
 * not shipped must not have a 28-day clock running. Measured and dismissed keep whatever is there.
 */
export async function moveOpportunities(ids: string[], status: SeoStatus) {
  if (!ids.length) return;
  const table = () => supabase.from('seo_opportunities');
  const unshipped = status === 'open' || status === 'diagnosed' || status === 'queued';
  const { error } = await table()
    .update(unshipped ? { status, shipped_at: null } : { status })
    .in('id', ids);
  if (error) throw error;
  if (status === 'shipped') {
    const stamp = await table().update({ shipped_at: new Date().toISOString() }).in('id', ids).is('shipped_at', null);
    if (stamp.error) throw stamp.error;
  }
}

async function callFunction<T>(name: string, payload: unknown): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not signed in');
  const res = await fetch(`/.netlify/functions/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  const body = (text ? JSON.parse(text) : {}) as T & { error?: string };
  if (!res.ok) throw new Error(body.error || text || `${name} failed: ${res.status}`);
  return body;
}

// Diagnosis is not here any more. It is an ai_jobs row of kind 'ideation' — see ideationJobs.ts.
// Detection stays a function because it is arithmetic over rows already in the database: no model,
// no credential, cheap enough to run nightly. Judgment needs the repo, so judgment goes to the queue.

/** Re-run detection. Cheap — it reads rows pages-sync already stored, no API calls and no units. */
export const runDetect = (clientId?: string | null) =>
  callFunction<{ total?: number; counts?: Record<string, number> }>('seo-detect', clientId ? { client_id: clientId } : {});
